import { ClientEvaluationContext, IState, ITraits } from "@flagsmith/flagsmith/types";
import { EvaluationContext, OpenFeature } from "@openfeature/web-sdk";
import { FlagsmithClientProvider } from "@openfeature/flagsmith-client-provider";

import { AuthService, User } from "./auth";
import { createApiClient } from "./api-client";
import { Organization } from "./contexts/organization";

const SDK_KEY = "LRmJvZbPBs7vTuKLXXQiyM";

export type AvailableFeatureFlag =
  | "permissions/organization-management"
  | "permissions/delete-balance-case"
  | "permissions/copy-carbon-report"
  | "permissions/carbon"
  | "license/action-plan"
  | "permissions/client-management"
  | "permissions/data-sources-overview"
  | "license/ai-assistant"
  | (string & {});

// We create our own provider here to set the identity context to transient.
// The code from here https://github.com/open-feature/js-sdk-contrib/blob/043c0680b7766aa8e730e991d0a9145a148f13c0/libs/providers/flagsmith-client/src/lib/flagsmith-client-provider.ts#L126
// is modified to add `transient: true` to the identity evaluation context.
// @ts-expect-error We override a private function here.
class TransientFlagsmithClientProvider extends FlagsmithClientProvider {
  private mapContextToEvaluationContext(
    context?: EvaluationContext & Partial<IState>,
    environmentID?: string
  ) {
    if (!context) {
      return {
        environment: {
          apiKey: environmentID,
        },
      };
    }

    const identity = context?.targetingKey;
    const traits = (context?.traits as ITraits) || {};
    const hasTraits = Object.keys(traits).length > 0;
    const hasIdentifier = !!identity;

    const evaluationContext: ClientEvaluationContext = {
      environment: {
        apiKey: SDK_KEY,
      },
      identity:
        hasIdentifier || hasTraits
          ? {
              transient: true,
              ...(hasIdentifier && { identifier: identity }),
              ...(hasTraits && { traits }),
            }
          : undefined,
    };

    return evaluationContext;
  }
}

let currentOrgaId: string | undefined;
let instancePromise: Promise<void> | undefined;
OpenFeature.setProvider(new TransientFlagsmithClientProvider({ environmentID: SDK_KEY }));

export const FeatureFlagService = {
  async reset(): Promise<void> {
    currentOrgaId = undefined;
    instancePromise = undefined;
    await OpenFeature.clearContext();
  },

  async setContext(orgaId: string | undefined): Promise<void> {
    if (orgaId === currentOrgaId && instancePromise) {
      return instancePromise;
    }
    currentOrgaId = orgaId;
    instancePromise = (async () => {
      const [user, organization] = await Promise.all([
        AuthService.user(),
        ((orgaId) => {
          if (orgaId) {
            const apiClient = createApiClient(AuthService.getTokenSilently, orgaId);
            return apiClient.getOrganizationById(orgaId);
          } else {
            return undefined;
          }
        })(orgaId),
      ]);
      if (user) {
        await OpenFeature.setContext(getContextAttributes(user, organization));
      }
    })().catch(() => {
      currentOrgaId = undefined;
      instancePromise = undefined;
    });
    return instancePromise;
  },

  isEnabled(flagName: AvailableFeatureFlag): boolean {
    return OpenFeature.getClient().getBooleanValue(flagName, false);
  },
};

export function getContextAttributes(user: User, organization?: Organization): EvaluationContext {
  const context: EvaluationContext = { targetingKey: user.id };
  if (user.email_verified) {
    context.traits = {
      co2: organization?.activeLicenses.includes("co2") ?? false,
      co2Trial: organization?.activeLicenses.includes("co2-trial") ?? false,
      actionPlan: organization?.activeLicenses.includes("action-plan") ?? false,
      aiAssistant: organization?.activeLicenses.includes("ai-assistant") ?? false,
    };
  }
  return context;
}
